import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { useProposalGenerationStore } from "../store/ProposalGenerationStore";
import {
  fetchEnrichedArtifacts,
  type ProposalGenerationDialogueData,
} from "../services/ProposalGenerationServices";
import { getMultipleDatasets } from "src/views/datasets/services/DatasetServices";
import type { EnrichedArtifact } from "src/reusable/Chat/PromptInput/ArtifactSelectorModal";
import type { ReportTemplate } from "../store/ProposalGenerationStore";
import { cleanArtifactFilename } from "src/utils/artifactUtils";

export interface StageInitializationData {
  isStageMode: boolean;
  stageRunId: number | null;
  stageNumber: number | null;
  stageDialogueId: number | undefined;
  stageDialogueData: ProposalGenerationDialogueData | undefined;
  templates: ReportTemplate[] | undefined;
}

export interface InitializationResult {
  success: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * The ChatStore half of a splash reset. Shared by initial-mode mount and
 * stage-to-stage re-initialization, which cleared the same four things
 * independently and drifted: only one of them cleared the persona.
 *
 * `resetToBaseConfig` is the call that clears `persona`, which ChatStore
 * persists in sessionStorage. Left set, it suppresses the incoming
 * resolution — `useApplyPersonaBySlug` skips while any persona is set — so
 * the chat runs the previous context's agent.
 */
function clearChatStoreForSplash(): void {
  const chatStore = useChatStore.getState();
  chatStore.setActiveDialogueId(null);
  chatStore.clearUploadedFiles();
  chatStore.setSourceDataRecordIds([]);
  chatStore.setDropdownDatasets([]);
  chatStore.resetToBaseConfig();
}

/**
 * Clear all state from ChatStore and ProposalGenerationStore
 * This ensures a clean slate for both initial mode and stage mode
 */
export function clearAllState(): void {
  clearChatStoreForSplash();
  useProposalGenerationStore.getState().reset();

  console.log("[SplashInit] State cleared");
}

/**
 * Initialize stage context (run ID, dialogue ID, report type, sequence)
 * Only relevant for stage mode
 */
export function initializeStageContext(data: StageInitializationData): void {
  if (!data.isStageMode || !data.stageDialogueData) return;

  const proposalStore = useProposalGenerationStore.getState();

  // Set run ID, report type, and sequence number
  if (data.stageRunId) {
    proposalStore.setRunId(data.stageRunId);
  }

  proposalStore.setReportType(data.stageDialogueData.report_type || null);
  proposalStore.setSequenceNumber(data.stageNumber || null);

  console.log("[SplashInit] Stage context initialized:", {
    runId: data.stageRunId,
    dialogueId: data.stageDialogueId,
    reportType: data.stageDialogueData.report_type,
    sequence: data.stageNumber,
  });
}

/**
 * Load and set template from previous stage
 * Non-blocking operation - if template not found, just log a warning
 */
export async function loadAndSetTemplate(
  data: StageInitializationData,
): Promise<void> {
  if (!data.isStageMode || !data.stageDialogueData) return;

  const proposalStore = useProposalGenerationStore.getState();
  const templateId = data.stageDialogueData.template_id;

  if (!templateId) {
    console.log("[SplashInit] No template to load");
    return;
  }

  const matchingTemplate = data.templates?.find((t) => t.id === templateId);

  if (matchingTemplate) {
    proposalStore.setSelectedTemplate({
      id: matchingTemplate.id,
      name: matchingTemplate.name,
      description: matchingTemplate.description,
    });
    console.log("[SplashInit] Template loaded:", matchingTemplate.name);
  } else {
    console.warn("[SplashInit] Template not found:", templateId);
  }
}

/**
 * Load and set datasets from previous stage
 * Blocking operation - throws error if fetch fails
 */
export async function loadAndSetDatasets(
  data: StageInitializationData,
): Promise<void> {
  if (!data.isStageMode || !data.stageDialogueData) return;

  const datasetIds = data.stageDialogueData.dataset_ids;

  if (!datasetIds || datasetIds.length === 0) {
    console.log("[SplashInit] No dataset_ids in stageDialogueData");
    return;
  }

  try {
    console.log("[SplashInit] Fetching datasets for IDs:", datasetIds);
    const fetchedDatasets = await getMultipleDatasets(datasetIds);

    if (fetchedDatasets && fetchedDatasets.length > 0) {
      const chatStore = useChatStore.getState();
      chatStore.setDropdownDatasets(fetchedDatasets);
    } else {
      console.warn("[SplashInit] No datasets fetched for IDs:", datasetIds);
    }
  } catch (error) {
    console.error("[SplashInit] Failed to load datasets:", error);
    throw new Error("Failed to load datasets from previous stage");
  }
}

/**
 * Load and set artifacts from previous stage
 * Non-blocking - returns empty array if no artifacts found
 * DOES NOT error if input_artifact_ids is empty (valid for some stages)
 */
export async function loadAndSetArtifacts(
  data: StageInitializationData,
): Promise<EnrichedArtifact[]> {
  if (!data.isStageMode || !data.stageDialogueData) return [];

  const artifactIds = data.stageDialogueData.input_artifact_ids;

  // Important: Don't error if empty - this is valid for some stage configurations
  if (!artifactIds || artifactIds.length === 0) {
    console.warn("[SplashInit] No input_artifact_ids in stageDialogueData");
    return [];
  }

  try {
    console.log("[SplashInit] Fetching artifacts for IDs:", artifactIds);
    const fetchedArtifacts = await fetchEnrichedArtifacts(artifactIds);

    if (fetchedArtifacts && fetchedArtifacts.length > 0) {
      const proposalStore = useProposalGenerationStore.getState();
      proposalStore.setSelectedArtifacts(fetchedArtifacts);

      // Convert artifacts to inputFiles format for immediate display
      const inputFiles = fetchedArtifacts.map((artifact) => ({
        name: cleanArtifactFilename(artifact.filename),
        size: artifact.filesize || 0,
        id: artifact.id,
        datasetId: artifact.dataset_id,
        sdrId: artifact.sdr_id,
        uploadProgress: 100,
        processingProgress: 100,
        uploadStatus: "uploaded" as const,
        processingStatus: "completed" as const,
      }));
      proposalStore.setInputFiles(inputFiles);
    } else {
      console.warn("[SplashInit] No artifacts fetched for IDs:", artifactIds);
    }

    return fetchedArtifacts || [];
  } catch (error) {
    console.error("[SplashInit] Failed to load artifacts:", error);
    throw new Error("Failed to load artifacts from previous stage");
  }
}

export function computeAndSetSourceDataRecordIds(
  data: StageInitializationData,
  loadedArtifacts: EnrichedArtifact[],
): void {
  if (!data.isStageMode || !data.stageDialogueData) return;

  const sdrIds: number[] = [];

  // Extract SDR IDs from loaded artifacts
  loadedArtifacts.forEach((artifact) => {
    if (artifact.sdr_id) {
      sdrIds.push(artifact.sdr_id);
    }
  });

  // Set to ChatStore
  const chatStore = useChatStore.getState();
  chatStore.setSourceDataRecordIds(sdrIds);
}

/**
 * Master orchestrator for stage mode initialization
 * Runs all initialization steps in sequence
 */
export async function initializeStageModeState(
  data: StageInitializationData,
): Promise<InitializationResult> {
  const errors: string[] = [];
  const warnings: string[] = [];

  try {
    // Step 1: Clear ChatStore and artifact-related ProposalGenerationStore
    // fields. Steps 3-5 reload the template, datasets and artifacts this
    // clears, from the stage being entered.
    clearChatStoreForSplash();

    const proposalStore = useProposalGenerationStore.getState();
    proposalStore.setInputFiles([]);
    proposalStore.setSelectedArtifacts([]);

    // Step 2: Initialize stage context
    initializeStageContext(data);

    // Step 3: Load template (non-blocking)
    try {
      await loadAndSetTemplate(data);
    } catch (error) {
      warnings.push("Failed to load template");
      console.warn("[SplashInit] Template load failed:", error);
    }

    // Step 4: Load datasets (blocking)
    try {
      await loadAndSetDatasets(data);
    } catch (error) {
      warnings.push("Failed to load datasets");
      console.warn("[SplashInit] Dataset load failed:", error);
      // Don't throw - datasets are optional if we have datasets from other sources
    }

    // Step 5: Load artifacts (non-blocking if empty)
    let loadedArtifacts: EnrichedArtifact[] = [];
    try {
      loadedArtifacts = await loadAndSetArtifacts(data);
    } catch (error) {
      warnings.push("Failed to load artifacts");
      console.warn("[SplashInit] Artifact load failed:", error);
      // Don't throw - empty artifacts is valid for some stages
    }

    // Step 6: Compute and set source data record IDs
    computeAndSetSourceDataRecordIds(data, loadedArtifacts);

    return {
      success: true,
      errors,
      warnings,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    errors.push(errorMessage);

    console.error("[SplashInit] Initialization failed:", error);

    return {
      success: false,
      errors,
      warnings,
    };
  }
}
